/** biome-ignore-all lint/correctness/useExhaustiveDependencies: legacy effect dep arrays preserved verbatim */
/** biome-ignore-all lint/suspicious/noEmptyBlockStatements: non-fatal catch, intentional no-op */
/**
 * Authenticated Layout
 *
 * Layout for authenticated pages with:
 * - BaseAppBar (navigation header)
 * - Snackbar notifications
 *
 * GlobalEventManager and UserFlowManager are rendered in RootLayout
 * to prevent unmounting during auth state transitions.
 *
 * This is the inner layout for /bbweb/* routes.
 */

import { useAuth } from '@bb/session-manager';
import { Box } from '@rocket/ui';
import { Outlet, useLocation } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { SnackbarCustom } from '../components';
import BaseAppBar from '../components/BaseAppBar/BaseAppBar';
import { TrialExpiredInterstitial } from '../components/subscription';
import { WebFtue, WebFtueProvider, WebResumableExtractionBanner } from '../features/Ftue';
import { setErrMsg, setSuccessMsg } from '../redux/actions/snackbarActions';
import { getSubscriptionStatus } from '../requests/subscription';
import { SUBSCRIPTION_UPGRADE_ROUTE } from '../routes.config';

export default function AuthenticatedLayout() {
  // biome-ignore lint/suspicious/noExplicitAny: redux state + axios are dynamic legacy shapes
  type AnyData = any;
  const successMsg = useSelector((state: AnyData) => state.Snackbar.successMsg, shallowEqual);
  const errMsg = useSelector((state: AnyData) => state.Snackbar.errMsg, shallowEqual);

  const dispatch = useDispatch();

  const setSuccess = (value: string | null) => dispatch(setSuccessMsg(value));
  const setErr = (value: string | null) => dispatch(setErrMsg(value));

  const { bbAxios } = useAuth() as { bbAxios: AnyData };
  const { pathname } = useLocation();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  useEffect(() => {
    getSubscriptionStatus()
      .then((data) => setSubscriptionStatus(data?.status))
      .catch(() => {}); // non-fatal — don't block the layout on failure
  }, [bbAxios]);

  const isTrialExpired =
    subscriptionStatus === 'trialing_expired' && pathname !== SUBSCRIPTION_UPGRADE_ROUTE;

  return (
    <WebFtueProvider>
      <Box height="100%">
        <BaseAppBar />

        {isTrialExpired && <TrialExpiredInterstitial />}

        <Box height="100vh" paddingTop="64px">
          <Outlet />
        </Box>

        {/* First-time-user experience — full-screen overlay for new users, dark-launched
            behind a flag + permission gate. Renders nothing otherwise. */}
        <WebFtue />

        {/* Returning-user counterpart: a rule-extraction job from a previous session
            may still be running (or just finished). WebFtue only mounts for
            first-time users, so this is the only surface a returning user has to
            ever see and resume it. Renders nothing otherwise. */}
        <WebResumableExtractionBanner />

        <SnackbarCustom severity="warning" msg={errMsg} setMsg={setErr} />
        <SnackbarCustom severity="success" msg={successMsg} setMsg={setSuccess} />
      </Box>
    </WebFtueProvider>
  );
}
